import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, FileText, Users, Clock, Lock, AlertCircle, DollarSign, Scale, HelpCircle } from 'lucide-react';
export default function Terms() {
  const [openSections, setOpenSections] = useState({
    definitions: true,
    instruction: true,
    duties: true,
    services: true,
    fees: true,
    timelines: true,
    dataProtection: true,
    intellectual: true,
    cancellation: true,
    liability: true,
    complaints: true,
    governing: true,
    amendments: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-805">
      <Helmet data-id="element-806">
        <title data-id="element-807">Terms & Conditions | Medicolegal Psychiatry</title>
        <meta name="description" content="Terms and conditions for Medicolegal Psychiatry services, outlining service agreements, obligations, data protection, and fees." data-id="element-808" />
        <meta name="keywords" content="Terms and Conditions, Service Agreement, Expert Witness Terms, Medicolegal Psychiatry" data-id="element-809" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-810">
        <div className="container mx-auto px-4" data-id="element-811">
          <div className="flex items-center justify-center mb-4" data-id="element-812">
            <Scale className="h-12 w-12 mr-4" data-id="element-813" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-814">
              Terms & Conditions
            </h1>
          </div>
        </div>
      </section>
      {/* Terms Content */}
      <section className="py-12" data-id="element-815">
        <div className="container mx-auto px-4" data-id="element-816">
          <div className="max-w-3xl mx-auto" data-id="element-817">
            <p className="text-slate-dark text-lg mb-8" data-id="element-818">
              These Terms and Conditions govern the provision of medico-legal
              psychiatric services by Dr. Imtiaz Syed (operating as "Medicolegal
              Psychiatry") to the instructing party. By instructing Dr. Syed,
              you are agreeing to these terms.
            </p>
            <div className="space-y-4 mb-8" data-id="element-819">
              {/* Definitions Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-820">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.definitions ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('definitions')} data-id="element-821">
                  <div className="flex items-center" data-id="element-822">
                    <FileText className="h-5 w-5 mr-2" data-id="element-823" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-824">
                      1. Definitions
                    </h2>
                  </div>
                  {openSections.definitions ? <ChevronUp className="h-5 w-5" data-id="element-825" /> : <ChevronDown className="h-5 w-5" data-id="element-826" />}
                </button>
                {openSections.definitions && <div className="p-6 bg-white" data-id="element-827">
                    <ul className="space-y-2 text-slate-dark" data-id="element-828">
                      <li data-id="element-829">
                        <strong data-id="element-830">"Dr. Syed"</strong> refers to Dr. Imtiaz Syed,
                        operating as Medicolegal Psychiatry.
                      </li>
                      <li data-id="element-831">
                        <strong data-id="element-832">"Instructing Party"</strong> refers to the
                        solicitor, law firm, court, tribunal, or other entity
                        that instructs Dr. Syed.
                      </li>
                      <li data-id="element-833">
                        <strong data-id="element-834">"Subject"</strong> refers to the individual who
                        is to be assessed by Dr. Syed.
                      </li>
                      <li data-id="element-835">
                        <strong data-id="element-836">"Services"</strong> refers to the medico-legal
                        psychiatric assessments, report writing, and expert
                        witness testimony provided by Dr. Syed.
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Instruction Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-837">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.instruction ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('instruction')} data-id="element-838">
                  <div className="flex items-center" data-id="element-839">
                    <FileText className="h-5 w-5 mr-2" data-id="element-840" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-841">
                      2. Instruction and Acceptance
                    </h2>
                  </div>
                  {openSections.instruction ? <ChevronUp className="h-5 w-5" data-id="element-842" /> : <ChevronDown className="h-5 w-5" data-id="element-843" />}
                </button>
                {openSections.instruction && <div className="p-6 bg-white" data-id="element-844">
                    <p className="text-slate-dark mb-4" data-id="element-845">
                      All instructions must be provided in writing, typically
                      via email to enquiries@medicolegal-psychiatry.co.uk. Dr.
                      Syed reserves the right to decline any instruction at his
                      discretion, particularly if there is a conflict of
                      interest or if the instruction falls outside his areas of
                      expertise.
                    </p>
                    <p className="text-slate-dark" data-id="element-846">
                      Upon acceptance of an instruction, Dr. Syed will provide a
                      Service Agreement outlining the specific services to be
                      provided, estimated timeframes, and fee structure. Work
                      will commence once this agreement has been signed and
                      returned by the Instructing Party.
                    </p>
                  </div>}
              </div>
              {/* Expert's Duties Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-847">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.duties ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('duties')} data-id="element-848">
                  <div className="flex items-center" data-id="element-849">
                    <Scale className="h-5 w-5 mr-2" data-id="element-850" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-851">
                      3. Expert's Duties
                    </h2>
                  </div>
                  {openSections.duties ? <ChevronUp className="h-5 w-5" data-id="element-852" /> : <ChevronDown className="h-5 w-5" data-id="element-853" />}
                </button>
                {openSections.duties && <div className="p-6 bg-white" data-id="element-854">
                    <p className="text-slate-dark" data-id="element-855">
                      Dr. Syed acknowledges and accepts the overriding duty to
                      the court as set out in the Civil Procedure Rules (CPR
                      Part 35), Family Procedure Rules (FPR Part 25), or
                      Criminal Procedure Rules (CrPR Part 19) as applicable. All
                      assessments and reports will be conducted with
                      impartiality, objectivity, and independence, regardless of
                      the instructing party.
                    </p>
                  </div>}
              </div>
              {/* Services Provided Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-856">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.services ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('services')} data-id="element-857">
                  <div className="flex items-center" data-id="element-858">
                    <Users className="h-5 w-5 mr-2" data-id="element-859" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-860">
                      4. Services Provided
                    </h2>
                  </div>
                  {openSections.services ? <ChevronUp className="h-5 w-5" data-id="element-861" /> : <ChevronDown className="h-5 w-5" data-id="element-862" />}
                </button>
                {openSections.services && <div className="p-6 bg-white" data-id="element-863">
                    <p className="text-slate-dark mb-4" data-id="element-864">
                      Services may include, but are not limited to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-865">
                      <li data-id="element-866">
                        Review of documentation provided by the Instructing
                        Party
                      </li>
                      <li data-id="element-867">
                        Psychiatric assessment of the Subject (remote or
                        in-person)
                      </li>
                      <li data-id="element-868">Preparation of medico-legal reports</li>
                      <li data-id="element-869">Attendance at conferences with counsel</li>
                      <li data-id="element-870">
                        Expert witness testimony in court or tribunal
                        proceedings
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-871">
                      The specific services to be provided will be detailed in
                      the Service Agreement.
                    </p>
                  </div>}
              </div>
              {/* Fees and Payment Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-872">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.fees ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('fees')} data-id="element-873">
                  <div className="flex items-center" data-id="element-874">
                    <DollarSign className="h-5 w-5 mr-2" data-id="element-875" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-876">
                      5. Fees and Payment
                    </h2>
                  </div>
                  {openSections.fees ? <ChevronUp className="h-5 w-5" data-id="element-877" /> : <ChevronDown className="h-5 w-5" data-id="element-878" />}
                </button>
                {openSections.fees && <div className="p-6 bg-white" data-id="element-879">
                    <p className="text-slate-dark mb-4" data-id="element-880">
                      Fees are calculated based on the complexity of the case,
                      volume of documentation, type of assessment required, and
                      estimated time involved. The fee structure will be
                      outlined in the Service Agreement and may include:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-881">
                      <li data-id="element-882">
                        Hourly rates for document review, report writing, and
                        conferences
                      </li>
                      <li data-id="element-883">Fixed fees for assessments</li>
                      <li data-id="element-884">Daily rates for court attendance</li>
                      <li data-id="element-885">Travel expenses and time (where applicable)</li>
                      <li data-id="element-886">
                        Cancellation fees (as per our Cancellation Policy)
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-887">
                      Payment terms are 30 days from the date of invoice. For
                      Legal Aid cases, Dr. Syed will work within the approved
                      funding limits, provided these are agreed upon in advance.
                    </p>
                  </div>}
              </div>
              {/* Timelines and Delivery */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-888">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.timelines ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('timelines')} data-id="element-889">
                  <div className="flex items-center" data-id="element-890">
                    <Clock className="h-5 w-5 mr-2" data-id="element-891" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-892">
                      6. Timelines and Delivery
                    </h2>
                  </div>
                  {openSections.timelines ? <ChevronUp className="h-5 w-5" data-id="element-893" /> : <ChevronDown className="h-5 w-5" data-id="element-894" />}
                </button>
                {openSections.timelines && <div className="p-6 bg-white" data-id="element-895">
                    <p className="text-slate-dark mb-4" data-id="element-896">
                      Dr. Syed aims to deliver reports within 10 working days of
                      the assessment, subject to the complexity of the case and
                      volume of documentation. Expedited reports (within 5
                      working days) may be available by prior arrangement and
                      may incur additional fees.
                    </p>
                    <p className="text-slate-dark" data-id="element-897">
                      While every effort will be made to meet agreed deadlines,
                      Dr. Syed cannot be held liable for delays resulting from
                      factors outside his control, such as the late provision of
                      documentation or the Subject's non-attendance.
                    </p>
                  </div>}
              </div>
              {/* Confidentiality and Data Protection */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-898">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataProtection ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataProtection')} data-id="element-899">
                  <div className="flex items-center" data-id="element-900">
                    <Lock className="h-5 w-5 mr-2" data-id="element-901" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-902">
                      7. Confidentiality and Data Protection
                    </h2>
                  </div>
                  {openSections.dataProtection ? <ChevronUp className="h-5 w-5" data-id="element-903" /> : <ChevronDown className="h-5 w-5" data-id="element-904" />}
                </button>
                {openSections.dataProtection && <div className="p-6 bg-white" data-id="element-905">
                    <p className="text-slate-dark mb-4" data-id="element-906">
                      Dr. Syed will handle all information in accordance with
                      the UK General Data Protection Regulation (UK GDPR), the
                      Data Protection Act 2018, and relevant professional
                      guidelines. Please refer to our Privacy Policy for
                      detailed information on how we collect, use, and protect
                      personal data.
                    </p>
                    <p className="text-slate-dark" data-id="element-907">
                      The Instructing Party is responsible for obtaining
                      appropriate consent from the Subject for the sharing of
                      their personal data with Dr. Syed for the purpose of
                      assessment and report preparation.
                    </p>
                  </div>}
              </div>
              {/* Intellectual Property */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-908">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.intellectual ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('intellectual')} data-id="element-909">
                  <div className="flex items-center" data-id="element-910">
                    <FileText className="h-5 w-5 mr-2" data-id="element-911" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-912">
                      8. Intellectual Property
                    </h2>
                  </div>
                  {openSections.intellectual ? <ChevronUp className="h-5 w-5" data-id="element-913" /> : <ChevronDown className="h-5 w-5" data-id="element-914" />}
                </button>
                {openSections.intellectual && <div className="p-6 bg-white" data-id="element-915">
                    <p className="text-slate-dark" data-id="element-916">
                      All reports and documents prepared by Dr. Syed remain his
                      intellectual property. The Instructing Party is granted a
                      license to use these documents for the specific legal
                      proceedings for which they were commissioned. Any other
                      use requires Dr. Syed's written permission.
                    </p>
                  </div>}
              </div>
              {/* Cancellation and Rescheduling */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-917">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.cancellation ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('cancellation')} data-id="element-918">
                  <div className="flex items-center" data-id="element-919">
                    <Clock className="h-5 w-5 mr-2" data-id="element-920" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-921">
                      9. Cancellation and Rescheduling
                    </h2>
                  </div>
                  {openSections.cancellation ? <ChevronUp className="h-5 w-5" data-id="element-922" /> : <ChevronDown className="h-5 w-5" data-id="element-923" />}
                </button>
                {openSections.cancellation && <div className="p-6 bg-white" data-id="element-924">
                    <p className="text-slate-dark" data-id="element-925">
                      Please refer to our separate Cancellation Policy for
                      detailed terms regarding the cancellation or rescheduling
                      of appointments and court attendance.
                    </p>
                  </div>}
              </div>
              {/* Liability */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-926">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.liability ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('liability')} data-id="element-927">
                  <div className="flex items-center" data-id="element-928">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-929" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-930">
                      10. Liability
                    </h2>
                  </div>
                  {openSections.liability ? <ChevronUp className="h-5 w-5" data-id="element-931" /> : <ChevronDown className="h-5 w-5" data-id="element-932" />}
                </button>
                {openSections.liability && <div className="p-6 bg-white" data-id="element-933">
                    <p className="text-slate-dark" data-id="element-934">
                      Dr. Syed maintains appropriate professional indemnity
                      insurance. His liability is limited to the fees paid for
                      the specific services provided. Dr. Syed is not liable for
                      any consequential, indirect, or special losses arising
                      from the provision of his services.
                    </p>
                  </div>}
              </div>
              {/* Complaints */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-935">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.complaints ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('complaints')} data-id="element-936">
                  <div className="flex items-center" data-id="element-937">
                    <HelpCircle className="h-5 w-5 mr-2" data-id="element-938" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-939">
                      11. Complaints
                    </h2>
                  </div>
                  {openSections.complaints ? <ChevronUp className="h-5 w-5" data-id="element-940" /> : <ChevronDown className="h-5 w-5" data-id="element-941" />}
                </button>
                {openSections.complaints && <div className="p-6 bg-white" data-id="element-942">
                    <p className="text-slate-dark" data-id="element-943">
                      If the Instructing Party is dissatisfied with any aspect
                      of Dr. Syed's services, they should first raise their
                      concerns directly with Dr. Syed. If the matter cannot be
                      resolved, the Instructing Party may refer to the relevant
                      professional body (e.g., General Medical Council, Royal
                      College of Psychiatrists) as appropriate.
                    </p>
                  </div>}
              </div>
              {/* Governing Law */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-944">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.governing ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('governing')} data-id="element-945">
                  <div className="flex items-center" data-id="element-946">
                    <Scale className="h-5 w-5 mr-2" data-id="element-947" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-948">
                      12. Governing Law
                    </h2>
                  </div>
                  {openSections.governing ? <ChevronUp className="h-5 w-5" data-id="element-949" /> : <ChevronDown className="h-5 w-5" data-id="element-950" />}
                </button>
                {openSections.governing && <div className="p-6 bg-white" data-id="element-951">
                    <p className="text-slate-dark" data-id="element-952">
                      These Terms and Conditions are governed by the laws of
                      England and Wales. Any disputes arising from these terms
                      shall be subject to the exclusive jurisdiction of the
                      courts of England and Wales.
                    </p>
                  </div>}
              </div>
              {/* Amendments */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-953">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.amendments ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('amendments')} data-id="element-954">
                  <div className="flex items-center" data-id="element-955">
                    <FileText className="h-5 w-5 mr-2" data-id="element-956" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-957">
                      13. Amendments
                    </h2>
                  </div>
                  {openSections.amendments ? <ChevronUp className="h-5 w-5" data-id="element-958" /> : <ChevronDown className="h-5 w-5" data-id="element-959" />}
                </button>
                {openSections.amendments && <div className="p-6 bg-white" data-id="element-960">
                    <p className="text-slate-dark" data-id="element-961">
                      Dr. Syed reserves the right to amend these Terms and
                      Conditions from time to time. The latest version will
                      always be available on our website. The version in effect
                      at the time of instruction will apply to that specific
                      case.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}